import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { LuBuilding2, LuCircleCheck, LuFolderKanban, LuFolderX, LuPlus, LuSearchX } from 'react-icons/lu';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import { EmptyState, SearchField } from '../../ProfilePage/components/ProfileUi';
import useTariffText from '../tariffText';

/**
 * "Hiç Tarife Eklenmemiş Projeler": every project without a tariff, with a
 * button that opens the tariff form for that customer and project.
 */
const ProjectsWithoutTariffsModal = ({ isOpen, projects, onAddTariff, onClose }) => {
  const { t, language, label } = useTariffText();
  const [search, setSearch] = useState('');
  const term = search.trim().toLocaleLowerCase('tr-TR');
  const visible = term
    ? projects.filter((project) => `${project.titleShort || ''} ${project.customerName || ''}`.toLocaleLowerCase('tr-TR').includes(term))
    : projects;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      title={label('withoutTariffs.title', { count: projects.length })}
      subtitle={label('withoutTariffs.subtitle')}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30" size="xs">
          <LuFolderX aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={<Button onClick={onClose}>{t('common.close')}</Button>}
    >
      {projects.length === 0 ? (
        <EmptyState icon={LuCircleCheck} title={label('withoutTariffs.allDone')} />
      ) : (
        <div className="tw-space-y-4">
          <SearchField value={search} onChange={setSearch} placeholder={label('withoutTariffs.search')} />
          {visible.length === 0 ? (
            <EmptyState icon={LuSearchX} title={label('withoutTariffs.noMatch')} compact />
          ) : (
            <ul className="tw-grid tw-gap-3 sm:tw-grid-cols-2 xl:tw-grid-cols-3">
              {visible.map((project) => (
                <li key={project.id} className="tw-flex tw-flex-col tw-gap-3 tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/80 tw-p-4 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                  <div className="tw-flex tw-items-start tw-gap-3">
                    <span className="tw-grid tw-h-9 tw-w-9 tw-shrink-0 tw-place-items-center tw-rounded-xl tw-bg-amber-500/10 tw-text-amber-600 dark:tw-text-amber-300">
                      <LuFolderKanban aria-hidden="true" className="tw-h-4 tw-w-4" />
                    </span>
                    <span className="tw-min-w-0">
                      <span className="tw-block tw-truncate tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{project.titleShort || '-'}</span>
                      <span className="tw-mt-0.5 tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                        <LuBuilding2 aria-hidden="true" className="tw-h-3 tw-w-3 tw-shrink-0" />
                        <span className="tw-truncate">{project.customerName || '-'}</span>
                      </span>
                    </span>
                  </div>
                  <Button size="sm" onClick={() => onAddTariff(project)} className="tw-self-start">
                    <LuPlus aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-text-teal-600 dark:tw-text-teal-300" />
                    {label('withoutTariffs.add')}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Modal>
  );
};

ProjectsWithoutTariffsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  projects: PropTypes.array.isRequired,
  onAddTariff: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ProjectsWithoutTariffsModal;
